import { type VariantProps, cva } from "class-variance-authority";
import type * as React from "react";

import { cn } from "@/utils/ui";
import { Slot } from "radix-ui";

const badgeVariants = cva(
  "inline-flex items-center justify-center border font-semibold w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none ring-gray-border-subtle/20 outline-gray-border-subtle/40 focus-visible:ring-4 focus-visible:outline-1 aria-invalid:focus-visible:ring-0 transition-[color,box-shadow]",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-accent-solid text-gray-fg-primary shadow-sm [a&]:hover:bg-accent-solid-hover",
        // secondary:
        //   "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive-solid text-gray-fg-primary shadow-sm [a&]:hover:bg-destructive-solid-hover",
        outline:
          "text-foreground [a&]:hover:bg-accent-solid [a&]:hover:text-gray-fg-primary",
        subtle:
          "border-transparent bg-graya-subtle-bg text-gray-fg-secondary [a&]:hover:bg-graya-ui",
      },
      size: {
        xs: "px-1 text-xxs/3",
        sm: "px-1 text-xs",
        md: "px-2 py-1 text-xs",
        lg: "px-4 py-1.5 text-base",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
);

function Badge({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : "span";

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
